import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import type { Testimonial, TestimonialFilter } from '@/components/ui/admin/testimonials';
import { apiClient } from '@/lib/api-client';
import { firstErrorMessage } from './term-versions';

export const testimonialKeys = {
  all: ['testimonials'] as const,
  lists: () => [...testimonialKeys.all, 'list'] as const,
  list: (params: TestimonialListParams) => [...testimonialKeys.lists(), params] as const,
  summary: (camp: string) => [...testimonialKeys.all, 'summary', camp] as const,
};

const testimonials = apiClient.testimonials as any;

/** Página da lista (a grade carrega mais ao rolar). */
export const TESTIMONIALS_PER_PAGE = 24;

type Page = {
  data: Testimonial[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
};

export type TestimonialListParams = {
  search: string;
  /** Rótulo do acampamento ("Aliste-se Forja 2025"); `''` = todos. */
  camp: string;
  filter: TestimonialFilter;
};

export type TestimonialSummary = {
  totals: { all: number; home: number; video: number };
  counts: Record<TestimonialFilter, number>;
  camps: string[];
};

/** Chama a API e troca o erro do eden pela mensagem da regra. */
async function call<T>(request: Promise<{ data: T }>) {
  try {
    return (await request).data;
  } catch (e: any) {
    throw firstErrorMessage(e?.value);
  }
}

/** Testemunhos em páginas, mais recentes primeiro. Busca, acampamento e filtro rodam na API. */
export function useTestimonialPages(params: TestimonialListParams) {
  return useInfiniteQuery({
    queryKey: testimonialKeys.list(params),
    initialPageParam: 1,
    // Trocar busca/filtro mantém a grade anterior até a nova chegar (sem piscar o skeleton).
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam }) =>
      call<Page>(
        testimonials.get({
          query: {
            page: pageParam,
            perPage: TESTIMONIALS_PER_PAGE,
            ...(params.search && { search: params.search }),
            ...(params.camp && { camp: params.camp }),
            filter: params.filter,
          },
        }),
      ),
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.totalPages
        ? last.pagination.page + 1
        : undefined,
  });
}

/** Contadores do topo e das pílulas + opções do select de acampamento. */
export function useTestimonialSummary(camp: string) {
  return useQuery({
    queryKey: testimonialKeys.summary(camp),
    queryFn: () =>
      call<TestimonialSummary>(testimonials.summary.get({ query: camp ? { camp } : {} })),
    placeholderData: keepPreviousData,
  });
}

/** Testemunhos já carregados (páginas achatadas). */
export const flattenTestimonials = (data?: InfiniteData<Page>) =>
  data?.pages.flatMap((p) => p.data) ?? [];

/**
 * Curadoria otimista: aplica `update` em todas as listas em cache (qualquer filtro) na hora e devolve
 * uma função que desfaz. Os contadores são recarregados da API (`refreshSummary`).
 */
export function useSetTestimonials() {
  const queryClient = useQueryClient();

  return useCallback(
    (update: (items: Testimonial[]) => Testimonial[]) => {
      const snapshots = queryClient.getQueriesData<InfiniteData<Page>>({
        queryKey: testimonialKeys.lists(),
      });
      queryClient.setQueriesData<InfiniteData<Page>>(
        { queryKey: testimonialKeys.lists() },
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((p) => ({ ...p, data: update(p.data) })),
          },
      );
      return () => {
        for (const [key, data] of snapshots) queryClient.setQueryData(key, data);
      };
    },
    [queryClient],
  );
}

export function useRefreshSummary() {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: [...testimonialKeys.all, 'summary'] }),
    [queryClient],
  );
}

/** Só `showOnHome` e `video` (`null` remove) — o texto não é editável pelo painel. */
export const patchTestimonials = (
  ids: string[],
  data: Pick<Partial<Testimonial>, 'showOnHome' | 'video'>,
) => Promise.all(ids.map((id) => call(testimonials({ id }).patch(data))));

export const deleteTestimonials = (ids: string[]) =>
  Promise.all(ids.map((id) => call(testimonials({ id }).delete())));

/**
 * Quadro do vídeo (1s, ou o meio se for mais curto) em JPEG — vira a miniatura do card.
 * Feito no navegador: o servidor não tem ffmpeg. `null` se o navegador não decodificar o arquivo (ex.: MOV/HEVC no Chrome).
 */
function capturePoster(file: File) {
  return new Promise<Blob | null>((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (blob: Blob | null) => {
      URL.revokeObjectURL(url);
      resolve(blob);
    };

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => done(null);
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, (video.duration || 0) / 2);
    };
    video.onseeked = () => {
      const scale = Math.min(1, 1280 / (video.videoWidth || 1280));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      if (!canvas.width || !canvas.height) return done(null);
      canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(done, 'image/jpeg', 0.82);
    };
    setTimeout(() => done(null), 15_000);
    video.src = url;
  });
}

/**
 * Envia o arquivo do vídeo (`POST /testimonials/video`, multipart) com progresso — o eden/fetch não
 * informa progresso de envio, por isso XHR. Devolve as URLs; o vínculo com o testemunho é o `PATCH`.
 */
export async function uploadTestimonialVideo(
  file: File,
  onProgress: (pct: number) => void,
) {
  const poster = await capturePoster(file);
  const body = new FormData();
  body.append('video', file);
  if (poster) body.append('poster', poster, 'poster.jpg');

  return new Promise<{ url: string; posterUrl: string | null }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/v1/testimonials/video');
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) =>
      e.lengthComputable && onProgress((e.loaded / e.total) * 100);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve(xhr.response)
        : reject(firstErrorMessage(xhr.response));
    xhr.onerror = () => reject(new Error('Falha no envio'));
    xhr.send(body);
  });
}
